import { initializeComscoreStreamingTag } from '../analytics';
import { stringify } from 'querystring';
import { defineMessages } from 'react-intl';
import { decorateIntlMessages } from '../../components/common/rosetta_formatted_message';
import { onProductionHostname } from '../env';
import {get, each} from 'lodash';
import type {
  AdData,
  Handlers,
  JWPlayerInstance,
  Props,
  EditionData,
  LocationData
} from './jw';
import {
  SOLD_CONTENT_TYPES,
  PRODUCTION_NIELSEN_VIDEO_APP_ID,
  DEVELOPMENT_NIELSEN_VIDEO_APP_ID,
  NIELSEN_VIDEO_APP_NAME,
  VICE_AD_NETWORK_ID
} from '../../constants';

const INTL_MESSAGES = decorateIntlMessages(__filename, defineMessages({
  jwShareCaption: {
    id: 'jw_share_caption',
    description: '"Share Video" caption on JW Player',
    defaultMessage: 'Share Video'
  }
}));

const getPrerollAdvertisingData = (ad_data: AdData, location: LocationData): Object =>
  ad_data.ad_disable_video_preroll ? {} : {
    client: 'googima',
    vpaidmode: 'enabled',
    vpaidcontrols: true,
    schedule: {
      'r29-schedule': {
        offset: 'pre',
        tag: 'https://pubads.g.doubleclick.net/gampad/ads?' + stringify({
          video_title: '__item-title__',
          video_id: '__item-mediaid__',
          url: '__page-url__',
          description_url: '__item-description__',
          sz: '4x4',
          impl: 's',
          iu: `${VICE_AD_NETWORK_ID}/r29/video_preroll`,
          gdfp_req: 1,
          env: 'vp',
          output: 'xml_vast3',
          cmsid: 918,
          unviewed_position_start: 1,
          correlator: '[timestamp]',
          cust_params: stringify({ // cust_params must be double URL encoded
            video_title: '__item-title__',
            video_id: '__item-mediaid__',
            is_sensitive_content: ad_data.ad_is_sensitive_content,
            sponsorship: ad_data.ad_sponsorship,
            test: location.query.test
          })
        }) + '&' // tag requires a trailing '&' character to parse correctly
      }
    }
  };

const nielsenContentMetaData = (
  content: Object,
  sanitizedTitle: string,
  ad_data: AdData
): Object => {
  return {
    type: 'content',
    assetid: content.asset_id || content.entry_asset_id,
    program: get(ad_data, 'ad_series') || 'Refinery29',
    title: sanitizedTitle,
    length: 60,
    isfullepisode: 'y',
    adloadtype: '2',
    crossId1: content.asset_id || content.entry_asset_id,
    mediaURL: `https://content.jwplatform.com/videos/${content.jw_video_id}-720.mp4`,
    hasAds: '1'
  };
};

const getJWVideoData = (
  content: Object,
  sanitizedTitle: string,
  preview_image: Object,
  ad_data: AdData
): Object => {
  const { jw_video_id } = content;
  return {
    sources: [{
      'file': `https://content.jwplatform.com/videos/${jw_video_id}-1920.mp4`,
      'label': '1080p'
    }, {
      'file': `https://content.jwplatform.com/videos/${jw_video_id}-1280.mp4`,
      'label': '720p',
      'default': 'true'
    }, {
      'file': `https://content.jwplatform.com/videos/${jw_video_id}-720.mp4`,
      'label': 'SD'
    }],
    image: get(preview_image, 'src')
           || `https://content.jwplatform.com/thumbs/${jw_video_id}-720.jpg`,
    title: sanitizedTitle,
    mediaid: jw_video_id,
    stream_custom_params: nielsenContentMetaData(content, sanitizedTitle, ad_data)
  };
};

const nielsenPluginConfiguration = (): Object => {
  return {
    sfcode: onProductionHostname() ? 'dcr' : 'dcr-cert',
    apid: onProductionHostname()
      ? PRODUCTION_NIELSEN_VIDEO_APP_ID
      : DEVELOPMENT_NIELSEN_VIDEO_APP_ID,
    apn: NIELSEN_VIDEO_APP_NAME,
    nsdkv: '511',
    nol_sdkDebug: ''
  };
};

const getPlugins = (): Object => {
  return {
    'https://cdn-gl.imrworldwide.com/novms/jw/ggjw510.js': nielsenPluginConfiguration()
  };
};

const shouldHideRelatedVideos = (sold_content_type: string): bool =>
  sold_content_type !== SOLD_CONTENT_TYPES.CUSTOM;

const setupJWPlayer = (
  jwPlayerInstance: JWPlayerInstance,
  props: Props,
  edition_data: EditionData,
  location: LocationData
): void => {
  const {
    adsState: { ad_data },
    content,
    intl: { formatMessage },
    preview_image,
    sanitizedTitle,
    sold_content_type
  } = props;
  const { jw_video_id } = content;
  const jwVideoData = getJWVideoData(content, sanitizedTitle, preview_image, ad_data);
  const jwPlayerConfiguration = {
    playlist: [jwVideoData],
    advertising: getPrerollAdvertisingData(ad_data, location),
    /*
     * Autoplay is configured in our JW Player dashboard
     * autostart: false,
     */
    sharing: {
      heading: formatMessage(INTL_MESSAGES.jwShareCaption),
      link: `https://content.jwplatform.com/previews/${jw_video_id}-${edition_data.config.jw_player_id}`,
      sites: ['facebook', 'twitter', 'pinterest', 'email', 'tumblr'],
      code: `%3Ciframe%20src%3D%22https%3A//content.jwplatform.com/players/${jw_video_id}-${edition_data.config.jw_player_id}.html%22%20width%3D%22320%22%20height%3D%22260%22%20frameborder%3D%220%22%20scrolling%3D%22auto%22%3E%3C/iframe%3E`
    },
    plugins: getPlugins(),
    related: {}
  };

  if (shouldHideRelatedVideos(sold_content_type)) {
    delete jwPlayerConfiguration.related;
  }

  jwPlayerInstance.setup(jwPlayerConfiguration);
};

export default (
  jwPlayerInstance: JWPlayerInstance,
  props: Props,
  handlers: Handlers,
  edition_data: EditionData,
  location: LocationData
): void => {
  initializeComscoreStreamingTag();
  setupJWPlayer(jwPlayerInstance, props, edition_data, location);
  each(handlers, (callback: Function, event: string): void =>
    jwPlayerInstance.on(event, callback)
  );
};
